import { message } from 'antd'
import { postJSON } from 'common/services/api'
import { useMutation, useQueryClient } from 'react-query'
import useMixpanelEvent, {
  MixpanelEvents
} from '../../../../../hooks/useMixpanelEvent'
import type { ArchiveDeviceRecordInput, DeviceRecord } from '../types'

interface UseDeviceRecordArchiveReturn {
  archiveDeviceRecord: (deviceRecordId: string) => Promise<DeviceRecord>
  unarchiveDeviceRecord: (deviceRecordId: string) => Promise<DeviceRecord>
  isArchiving: boolean
}

export const useDeviceRecordArchive = (
  residentId: string,
  onSuccess?: () => void
): UseDeviceRecordArchiveReturn => {
  const queryClient = useQueryClient()
  const sendMixpanelEvent = useMixpanelEvent()

  const archiveMutation = useMutation(
    async ({
      deviceRecordId,
      archive
    }: {
      deviceRecordId: string
      archive: boolean
    }) => {
      const requestBody: ArchiveDeviceRecordInput = { isArchived: archive }

      const response = await postJSON({
        url: `/v6/residents/device-records/${deviceRecordId}/archive`,
        method: 'PUT',
        json: requestBody
      })

      return response.data as DeviceRecord
    },
    {
      onSuccess: (_, variables) => {
        sendMixpanelEvent(
          variables.archive
            ? MixpanelEvents.DEVICE_RECORD_ARCHIVED
            : MixpanelEvents.DEVICE_RECORD_UNARCHIVED,
          {
            residentId,
            deviceRecordId: variables.deviceRecordId
          }
        )
        message.success(
          variables.archive
            ? 'Device record archived successfully'
            : 'Device record restored successfully'
        )
        queryClient.invalidateQueries(['device-records', residentId])
        if (onSuccess) {
          onSuccess()
        }
      },
      onError: (error: any) => {
        message.error(error?.message || 'Failed to update device record')
      }
    }
  )

  const archiveDeviceRecord = async (
    deviceRecordId: string
  ): Promise<DeviceRecord> => {
    return archiveMutation.mutateAsync({ deviceRecordId, archive: true })
  }

  const unarchiveDeviceRecord = async (
    deviceRecordId: string
  ): Promise<DeviceRecord> => {
    return archiveMutation.mutateAsync({ deviceRecordId, archive: false })
  }

  return {
    archiveDeviceRecord,
    unarchiveDeviceRecord,
    isArchiving: archiveMutation.isLoading
  }
}
